import { useMemo, useState } from 'react'
import { PageHeader } from '../components/PageHeader'
import {
  ArmStateChip,
  Button,
  Chip,
  EmptyState,
  Input,
  Skeleton,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '../components/ui'
import { ZoneEditorModal } from '../components/features/ZoneEditorModal'
import { useLocationTree, useZones } from '../api/hooks'
import { locationNames } from '../lib/locations'
import { ZONE_KINDS, ZONE_KIND_TONE, zoneLocationSummary } from '../lib/zones'
import type { Zone } from '../api/types'
import { SelectField } from './Directory'

function errText(e: unknown): string {
  return e instanceof Error ? e.message : String(e)
}

/**
 * Every zone of the customer (alarm areas, anti-passback, rollcall, …) with
 * the locations it spans. A zone can cover any number of places at any tree
 * level; create/edit/delete all go through the shared ZoneEditorModal
 * (the same editor the Locations card opens).
 */
export default function ZonesPage() {
  const zones = useZones()
  const tree = useLocationTree()
  const names = useMemo(() => locationNames(tree.data ?? []), [tree.data])

  const [kindFilter, setKindFilter] = useState<string>('all')
  const [search, setSearch] = useState('')
  const [editorOpen, setEditorOpen] = useState(false)
  const [editing, setEditing] = useState<Zone | null>(null)

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase()
    return (zones.data ?? [])
      .filter((z) => kindFilter === 'all' || z.kind === kindFilter)
      .filter(
        (z) =>
          !q ||
          z.name.toLowerCase().includes(q) ||
          z.locations.some((uid) => (names.get(uid) ?? '').toLowerCase().includes(q)),
      )
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [zones.data, kindFilter, search, names])

  const openCreate = () => {
    setEditing(null)
    setEditorOpen(true)
  }
  const openEdit = (z: Zone) => {
    setEditing(z)
    setEditorOpen(true)
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader section="Places" title="Zones" action={<Button onClick={openCreate}>+ Zone</Button>} />

      <div className="flex flex-wrap items-end gap-4">
        <div className="w-56">
          <SelectField
            label="Kind"
            value={kindFilter}
            onChange={setKindFilter}
            options={[{ value: 'all', label: 'All kinds' }, ...ZONE_KINDS.map((k) => ({ value: k.value, label: k.label }))]}
          />
        </div>
        <div className="w-72">
          <Input
            label="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Zone or location name"
          />
        </div>
      </div>

      {zones.isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-12" />
          <Skeleton className="h-12" />
          <Skeleton className="h-12" />
        </div>
      ) : zones.isError ? (
        <EmptyState title="Could not load zones" hint={errText(zones.error)} />
      ) : zones.data.length === 0 ? (
        <EmptyState
          title="No zones yet"
          hint="A zone groups places — e.g. an alarm area spanning several addresses and floors."
          action={
            <Button size="sm" onClick={openCreate}>
              + Zone
            </Button>
          }
        />
      ) : visible.length === 0 ? (
        <EmptyState title="No matching zones" hint="Clear the search or pick another kind." />
      ) : (
        <Table>
          <THead>
            <tr>
              <TH>Zone</TH>
              <TH>Kind</TH>
              <TH>State</TH>
              <TH>Locations</TH>
              <TH className="text-right">Action</TH>
            </tr>
          </THead>
          <TBody>
            {visible.map((z) => (
              <TR key={z.unique_id}>
                <TD>
                  <button
                    type="button"
                    onClick={() => openEdit(z)}
                    className="text-left focus:outline-none focus:ring-1 focus:ring-ostrel-gold"
                  >
                    <span className="block text-sm font-medium text-ostrel-ink hover:underline">{z.name}</span>
                    <span className="tnum block font-mono text-[10px] text-ostrel-ink-muted">{z.unique_id}</span>
                  </button>
                </TD>
                <TD>
                  <Chip tone={ZONE_KIND_TONE[z.kind] ?? 'muted'} label={z.kind.replace(/_/g, ' ')} />
                </TD>
                <TD>
                  <ArmStateChip state={z.arm_state} />
                </TD>
                <TD>
                  <span
                    className="text-xs text-ostrel-ink-muted"
                    title={z.locations.map((uid) => names.get(uid) ?? uid).join(', ')}
                  >
                    {zoneLocationSummary(z, names)}
                  </span>
                </TD>
                <TD className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(z)}>
                    Edit
                  </Button>
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}

      <ZoneEditorModal open={editorOpen} zone={editing} onClose={() => setEditorOpen(false)} />
    </div>
  )
}
